import React, { useState } from "react";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

function formatAmount(amount) {
  const n = Number(amount);
  if (Number.isNaN(n)) return String(amount ?? "");
  return Number.isInteger(n) ? String(n) : String(n);
}

function IngredientList({ group }) {
  return (
    <Box component="ul" sx={{ m: 0, px: 2.5, py: 1.25, pl: 3.5 }}>
      {(group.items || []).map((item) => {
        const note = (item.note || "").trim();
        return (
          <Typography
            component="li"
            key={item.item_uuid || `${item.ingredient_name}-${item.amount}-${item.unit}`}
            variant="body2"
            sx={{ mb: 0.45, lineHeight: 1.55 }}
          >
            <Box component="strong" sx={{ fontWeight: 700 }}>
              {item.ingredient_name}
            </Box>
            {`, ${formatAmount(item.amount)} ${item.unit}`}
            {note ? ` — ${note}` : null}
          </Typography>
        );
      })}
    </Box>
  );
}

/**
 * Ingredient group card — collapsible inline, or static when collapsible={false}.
 */
export function IngredientGroupCard({
  group,
  defaultExpanded = false,
  collapsible = true,
          titlePrefix = "Ingredienser til",
}) {
  const [expanded, setExpanded] = useState(defaultExpanded || !collapsible);
  if (!group) return null;
  const name = group.name || "Ingredienser";
  const title = collapsible ? `${titlePrefix} ${name}` : name;

  return (
    <Box
      className="ingredient-group-card"
      sx={{
        my: 1.5,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1.5,
        overflow: "hidden",
        bgcolor: "background.paper",
      }}
    >
      {collapsible ? (
        <Box
          component="button"
          type="button"
          onClick={() => setExpanded((v) => !v)}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            width: "100%",
            textAlign: "left",
            border: 0,
            bgcolor: "grey.50",
            px: 1.5,
            py: 1.1,
            cursor: "pointer",
            font: "inherit",
            color: "inherit",
            "&:hover": { bgcolor: "grey.100" },
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1 }}>
            {title}
          </Typography>
          <IconButton
            size="small"
            tabIndex={-1}
            aria-label={expanded ? "Collapse" : "Expand"}
            sx={{
              transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s",
            }}
          >
            <ExpandMoreIcon fontSize="small" />
          </IconButton>
        </Box>
      ) : (
        <Box sx={{ bgcolor: "grey.50", px: 1.5, py: 1.1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {title}
          </Typography>
        </Box>
      )}
      {collapsible ? (
        <Collapse in={expanded}>
          <IngredientList group={group} />
        </Collapse>
      ) : (
        <IngredientList group={group} />
      )}
    </Box>
  );
}

export default IngredientGroupCard;
